import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Drawer, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Box, IconButton,
  CssBaseline, Chip, Button, Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CalendarToday as ScheduleIcon,
  Download as ExportsIcon,
  Settings as AdminIcon,
  Hub as TemIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Lock as LockIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const DRAWER_WIDTH = 240;

// ── Classification banner (reference-site style, top + bottom) ──
const BANNER_TEXT = 'UNCLASSIFIED//TEST; ACCEPTANCE';
const BANNER_HEIGHT = 22;

function ClassificationBanner({ position }: { position: 'top' | 'bottom' }) {
  return (
    <Box
      component="div"
      role="banner"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        [position]: 0,
        height: BANNER_HEIGHT,
        zIndex: 1300,
        backgroundColor: '#007a33',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        borderTop: position === 'bottom' ? '1px solid #00b84d' : undefined,
        borderBottom: position === 'top' ? '1px solid #00b84d' : undefined,
        userSelect: 'none',
        pointerEvents: 'none',
      }}
    >
      {BANNER_TEXT}
    </Box>
  );
}

const NAV_ITEMS = [
  { path: '/main', label: 'Main', icon: <DashboardIcon />, matchPaths: ['/', '/main'] },
  { path: '/schedule', label: 'Schedule', icon: <ScheduleIcon />, matchPaths: ['/schedule'] },
  { path: '/exports', label: 'Exports', icon: <ExportsIcon />, matchPaths: ['/exports'] },
  { path: '/admin', label: 'Admin', icon: <AdminIcon />, matchPaths: ['/admin'] },
  { path: '/tem', label: 'T-Rex', icon: <TemIcon />, matchPaths: ['/tem'] },
  { path: '/info', label: 'Info', icon: <InfoIcon />, matchPaths: ['/info'] },
];


interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  // Pages that own the viewport (no page scroll): the map and the calendar.
  const isMapPage = ['/', '/main', '/schedule'].includes(location.pathname);

  const reopenLogin = () => {
    localStorage.removeItem('aussidex_readonly_dismissed');
    window.location.reload();
  };

  const drawer = (
    <Box>
      <Toolbar sx={{ background: 'linear-gradient(135deg, #212229 0%, #12131b 100%)' }}>
        <Typography variant="h6" noWrap component="div" sx={{ color: '#f72bef', fontWeight: 'bold' }}>
          AUSS<span style={{ color: '#00b8ff' }}>IDEX</span>
        </Typography>
      </Toolbar>
      <List>
        {NAV_ITEMS.map((item) => {
          const selected = item.matchPaths.includes(location.pathname);
          return (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={selected}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(196, 46, 190, 0.15)',
                  borderRight: '3px solid #c42ebe',
                },
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' }
              }}
            >
              <ListItemIcon sx={{ color: selected ? '#c42ebe' : '#8a90a0' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} sx={{ color: '#e0e0e0' }} />
            </ListItemButton>
          </ListItem>
          );
        })}

      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <ClassificationBanner position="top" />
      <AppBar
        position="fixed"
        sx={{
          top: BANNER_HEIGHT,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar variant={isMapPage ? 'dense' : 'regular'}>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {NAV_ITEMS.find(i => i.matchPaths.includes(location.pathname))?.label || 'AUSSIDEX'}
          </Typography>
          {user ? (
            <>
              <Chip
                size="small"
                label={`${user.email}${user.roles.includes('admin') ? ' · admin' : ''}`}
                sx={{ mr: 1, backgroundColor: 'rgba(0,184,255,0.15)', color: '#00b8ff' }}
              />
              <Tooltip title="Log out">
                <IconButton color="inherit" onClick={logout} size="small">
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Read-only mode — log in to enable mission changes">
              <Button color="inherit" size="small" startIcon={<LockIcon />} onClick={reopenLogin}>
                Log In
              </Button>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, backgroundColor: '#1a1c26', borderRight: '1px solid #333', top: BANNER_HEIGHT, height: `calc(100% - ${BANNER_HEIGHT}px)` },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, backgroundColor: '#1a1c26', borderRight: '1px solid #333', top: BANNER_HEIGHT, height: `calc(100% - ${BANNER_HEIGHT}px)` },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={isMapPage ? {
          // Map page: fill the viewport exactly between the banners, no page scroll.
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          pt: `${BANNER_HEIGHT}px`,
          pb: `${BANNER_HEIGHT}px`,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          backgroundColor: '#12131b',
        } : {
          flexGrow: 1,
          p: 3,
          pt: `calc(${BANNER_HEIGHT}px + 12px)`,
          pb: `calc(${BANNER_HEIGHT}px + 24px)`,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          backgroundColor: '#12131b',
          minHeight: '100vh',
        }}
      >
        <Toolbar variant={isMapPage ? 'dense' : 'regular'} />
        {children}
      </Box>
      <ClassificationBanner position="bottom" />
    </Box>
  );
}
